import React, {useEffect, useMemo, useState} from 'react';
import {categoryApi} from "../../api/CategoriesService";
import Loader from "../ui/Loader";
import BulkUploadRowGroup from "./BulkUploadRowGroup";
import {IBulkUploadRow} from "../../model/IBulkUpload";
import CategoryPicker from "../ui/transaction/CategoryPicker";
import {TransactionType} from "../../model/TransactionType";
import Filter from "../ui/Filter";
import StatusFilters from "../ui/bulk_upload/StatusFilters";
import CategoryFilledCheckbox from "../ui/bulk_upload/FilledRowCheckbox";
import CommentField from "../ui/transaction/CommentField";
import {Tag} from "../../model/ICategory";
import TagsPicker from "../ui/transaction/TagsPicker";

interface SessionProps {
    session_id: string;
}

const BulkUploadRowsTable = (props: SessionProps) => {
    const session_id = props.session_id;
    const {data: sessionRows, isLoading, error, refetch: refetchSessionRows} = categoryApi.useFetchAllBulkUploadRowsQuery(session_id);
    const {data: session, isLoading: sessionLoading} = categoryApi.useGetBulkUploadSessionQuery(session_id);
    const {data: expenseCategories, isLoading: expCatLoading} = categoryApi.useFetchAllCategoriesQuery(TransactionType.Expense);
    const {data: incomeCategories, isLoading: incCatLoading} = categoryApi.useFetchAllCategoriesQuery(TransactionType.Income);
    const {data: tagsList} = categoryApi.useFetchAllTagsQuery();
    const [category, setCategory] = useState(null as any);
    const [tags, setTags] = useState([] as Tag[]);
    const [comment, setComment] = useState("");
    const [categoryType, setCategoryType] = useState(null as any);
    const [filter, setFilter] = useState('');
    const [selectFilled, setSelectFilled] = useState(true);
    const [showBlocked, setShowBlocked] = useState(true);
    const [showOperations, setShowOperations] = useState(true);
    const [selectedStatuses, setSelectedStatuses] = useState([
        'UPLOADED', 'DUPLICATE', 'EXCLUDED', 'CREATED', 'UPDATE', 'UPDATED', 'SPLIT', 'SPLITED', 'UNITE', 'UNITED',
    ]);
    const [patchBulkUploadRows, {isLoading: isPatching}] = categoryApi.usePatchBulkUploadRowsMutation();
    const [uniteBulkUploadRows, {isLoading: isUniting}] = categoryApi.useUniteBulkUploadRowsMutation();
    const [reconcileBulkUploadSession, {isLoading: isReconciling}] = categoryApi.useReconcileBulkUploadSessionMutation();

    const [allSelected, setAllSelected] = useState(false);
    const [selected, setSelected] = useState([] as IBulkUploadRow[]);

    const childrenByParent = useMemo(() => {
        const map = new Map<number, IBulkUploadRow[]>();
        if (!sessionRows) {
            return map;
        }
        sessionRows.forEach((row) => {
            if (!row.parent_row) {
                return;
            }
            const list = map.get(row.parent_row) || [];
            list.push(row);
            map.set(row.parent_row, list);
        });
        map.forEach((list, parentId) => {
            map.set(parentId, [...list].sort((a, b) => a.id - b.id));
        });
        return map;
    }, [sessionRows]);

    const filteredRows = useMemo(() => {
        if (!sessionRows) {
            return [];
        }
        const filterInUpper = filter.toUpperCase();
        return [...sessionRows].filter((row) =>
            selectedStatuses.includes(row.status)
            && (selectFilled || !(
                row.category
                || row.account_to
            ))
            && (row.is_blocked ? showBlocked : showOperations)
            && (filter === ''
                || row.currency_code.toUpperCase().includes(filterInUpper)
                || row.description.toUpperCase().includes(filterInUpper)
                || row.category_description && row.category_description.toUpperCase().includes(filterInUpper)));
    }, [sessionRows, filter, selectedStatuses, selectFilled, showBlocked, showOperations]);

    const displayGroups = useMemo(() => {
        if (!sessionRows) {
            return [] as {parent: IBulkUploadRow, children: IBulkUploadRow[]}[];
        }

        const rowsById = new Map(sessionRows.map((row) => [row.id, row]));
        const filteredIds = new Set(filteredRows.map((row) => row.id));
        const parentIdsToShow = new Set<number>();

        const isHiddenFilledUniteParent = (parent: IBulkUploadRow | undefined) =>
            !!parent
            && (parent.status === 'UNITE' || parent.status === 'UNITED')
            && !selectFilled
            && !!parent.category;

        const isHiddenFilledSplitParent = (parent: IBulkUploadRow | undefined) => {
            if (!parent || selectFilled) {
                return false;
            }
            if (parent.status !== 'SPLIT' && parent.status !== 'SPLITED') {
                return false;
            }
            const children = childrenByParent.get(parent.id) || [];
            return children.length > 0 && children.every((child) => !!child.category);
        };

        const isHiddenFilledParent = (parent: IBulkUploadRow | undefined) =>
            isHiddenFilledUniteParent(parent) || isHiddenFilledSplitParent(parent);

        filteredRows.forEach((row) => {
            if (row.parent_row) {
                const parent = rowsById.get(row.parent_row);
                if (isHiddenFilledParent(parent)) {
                    return;
                }
                parentIdsToShow.add(row.parent_row);
            } else {
                parentIdsToShow.add(row.id);
            }
        });

        // Keep parent visible when any of its children match filters.
        childrenByParent.forEach((children, parentId) => {
            const parent = rowsById.get(parentId);
            if (isHiddenFilledParent(parent)) {
                return;
            }
            if (children.some((child) => filteredIds.has(child.id))) {
                parentIdsToShow.add(parentId);
            }
        });

        const roots = sessionRows
            .filter((row) => !row.parent_row && parentIdsToShow.has(row.id))
            .filter((row) => !isHiddenFilledParent(row))
            .sort((a, b) => {
                const dateDiff = new Date(a.date).getTime() - new Date(b.date).getTime();
                return dateDiff !== 0 ? dateDiff : a.id - b.id;
            });

        return roots.map((parent) => ({
            parent,
            children: (childrenByParent.get(parent.id) || []).filter((child) =>
                parent.status === 'SPLIT'
                || parent.status === 'SPLITED'
                || parent.status === 'UNITE'
                || parent.status === 'UNITED'
                || filteredIds.has(child.id)
            ),
        }));
    }, [sessionRows, filteredRows, childrenByParent, selectFilled]);

    const selectableRows = useMemo(() => {
        const rows: IBulkUploadRow[] = [];
        displayGroups.forEach(({parent, children}) => {
            const isUniteParent = parent.status === 'UNITE' || parent.status === 'UNITED';
            if (parent.status !== 'SPLIT' && parent.status !== 'SPLITED' && !isUniteParent) {
                rows.push(parent);
            }
            if (!isUniteParent) {
                children.forEach((child) => rows.push(child));
            }
        });
        return rows;
    }, [displayGroups]);

    const selectedIds = useMemo(() => new Set(selected.map((row) => row.id)), [selected]);

    const isExpenseOrIncome = (type: string | undefined | null) =>
        type === TransactionType.Expense || type === TransactionType.Income;

    const isUniteSelection = useMemo(() => {
        if (selected.length < 2) {
            return false;
        }
        const eligible = selected.filter((row) =>
            !row.parent_row
            && row.status === 'UPLOADED'
            && isExpenseOrIncome(row.category_type)
        );
        if (eligible.length < 2 || eligible.length !== selected.length) {
            return false;
        }
        const hasExpense = eligible.some((row) => row.category_type === TransactionType.Expense);
        const hasIncome = eligible.some((row) => row.category_type === TransactionType.Income);
        return hasExpense && hasIncome;
    }, [selected]);

    const handleSelectAll = (e: React.FormEvent<HTMLInputElement>) => {
        const isAllSelected = e.currentTarget.checked;
        console.log("handleSelectAll" + isAllSelected);

        if (allSelected) {
            setSelected([]);
            setAllSelected(false);
            setCategoryType(null);
        } else {
            if (selectableRows.length === 0) {
                alert('No rows to select');
                return;
            }
            const uniteCandidates = selectableRows.filter((row) =>
                !row.parent_row
                && row.status === 'UPLOADED'
                && isExpenseOrIncome(row.category_type)
            );
            const allExpenseOrIncome = selectableRows.every((row) => isExpenseOrIncome(row.category_type));
            const firstType = selectableRows[0].category_type;
            if (selectableRows.every((row) => row.category_type === firstType)) {
                setSelected(selectableRows);
                setAllSelected(true);
                setCategoryType(firstType);
            } else if (allExpenseOrIncome && uniteCandidates.length >= 2) {
                setSelected(uniteCandidates);
                setAllSelected(true);
                setCategoryType(uniteCandidates[0].category_type);
            } else {
                alert('All rows should be the same category type');
                return;
            }
        }
    };

    const handleCategoryRowChange = (e: React.FormEvent<HTMLInputElement>) => {
        const categoryId = e.currentTarget.value;
        console.log("handleCategoryRowChange: " + categoryId);
        if (!sessionRows) {
            return;
        }

        const list = [...selected];
        const rowToUnselect = list.find(row => row.id.toString() === categoryId)
        const deleteRowIndex = rowToUnselect ? list.indexOf(rowToUnselect) : -1;
        if (deleteRowIndex === -1) {
            const rowToSelect = sessionRows.find(row => row.id.toString() === categoryId)
            if (!rowToSelect) {
                return;
            }
            if (rowToSelect.status === 'SPLIT' || rowToSelect.status === 'SPLITED' || rowToSelect.status === 'UNITE' || rowToSelect.status === 'UNITED') {
                return;
            }
            const parentOfRow = rowToSelect.parent_row
                ? sessionRows.find((row) => row.id === rowToSelect.parent_row)
                : undefined;
            if (parentOfRow && (parentOfRow.status === 'UNITE' || parentOfRow.status === 'UNITED')) {
                return;
            }
            if (categoryType && rowToSelect.category_type !== categoryType) {
                const canMixExpenseIncome =
                    isExpenseOrIncome(categoryType)
                    && isExpenseOrIncome(rowToSelect.category_type)
                    && !rowToSelect.parent_row
                    && rowToSelect.status === 'UPLOADED'
                    && list.every((row) => !row.parent_row && row.status === 'UPLOADED');
                if (!canMixExpenseIncome) {
                    alert("Please choose row with the same category type");
                    return;
                }
            }
            list.push(rowToSelect);
            if (!categoryType) {
                setCategoryType(rowToSelect.category_type);
            }
        } else {
            list.splice(deleteRowIndex, 1);
            if (list.length === 0) {
                setCategoryType(null);
            }
        }
        setSelected(list);
    };

    useEffect(() => {
        setSelected([]);
        setAllSelected(false);
        setCategoryType(null);
        setCategory(null);
    }, [filteredRows]);

    const handleBulkUpdate = (e: React.MouseEvent<HTMLElement>) => {
        console.log(selected);
        if (!category) {
            alert("Please choose Category");
            return;
        }
        if (selected.length === 0) {
            alert("Please choose at least one row");
            return;
        }
        patchBulkUploadRows(selected.map(selectedRow => ({id: selectedRow.id, category: category.id, comment: comment, tag_ids: tags.map(tag => tag.id)})));

        setSelected([]);
    }

    const handleUnite = () => {
        if (!isUniteSelection) {
            return;
        }
        uniteBulkUploadRows(selected.map((row) => row.id));
        setSelected([]);
        setAllSelected(false);
        setCategoryType(null);
        setCategory(null);
    }

    const handleReconcile = (e: React.MouseEvent<HTMLElement>) => {
        console.log('Call Recons with session_id: '+ session_id);
        reconcileBulkUploadSession(session_id);
        refetchSessionRows();
    }

    return (
        <>
            <div className="sticky top-16 bg-gray-50 rounded-md z-40">
                <div className="mx-10 my-2 px-6 pt-4 pb-2 flex justify-evenly items-end">
                    <div className="flex justify-center items-end">
                        {isUniteSelection ? (
                            <button className="mx-10 h-10 group relative flex justify-center py-2 px-6 border border-transparent
                                               text-sm font-medium rounded-md bg-indigo-100 text-indigo-800 hover:bg-indigo-200 focus:outline-none"
                                    disabled={isLoading || isUniting}
                                    onClick={handleUnite}>
                                Unite
                            </button>
                        ) : (
                            <>
                                <CategoryPicker label={"Category"} category={category} setCategory={setCategory}
                                                categories={
                                                    categoryType === TransactionType.Expense ? expenseCategories
                                                        : categoryType === TransactionType.Income ? incomeCategories
                                                        : []
                                                }
                                                className="mx-4"/>
                                <CommentField label={"Comment"} comment={comment} setComment={setComment} className="mx-4"/>
                                <TagsPicker label="Tags" tags={tags} setTags={setTags} tagsList={tagsList ? tagsList : []} className="mx-4"/>
                                <button className="mx-10 h-10 group relative flex justify-center py-2 px-6 border border-transparent
                                                   text-sm font-medium rounded-md bg-blue-100 text-blue-800 hover:bg-blue-200 focus:outline-none"
                                        disabled={isLoading || isPatching}
                                        onClick={handleBulkUpdate}>
                                    Bulk Update
                                </button>
                            </>
                        )}
                    </div>
                    <button className="mx-10 my-1 h-10 group relative flex justify-center items-center py-2 px-6 border border-transparent
                                       text-sm font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none"
                            disabled={isReconciling}
                            onClick={handleReconcile}>
                        Reconcile
                    </button>
                </div>

                <hr className="gray-900"/>

                <div className="mx-10 my-2 px-6 pt-2 pb-4 flex items-end justify-evenly">
                    <Filter filter={filter} setFilter={setFilter}/>
                    <CategoryFilledCheckbox selectFilled={selectFilled} setSelectFilled={setSelectFilled} className="m-2"/>
                    <StatusFilters selectedStatuses={selectedStatuses} setSelectedStatuses={setSelectedStatuses} className="m-2"/>
                    <div className="m-2">
                        <label className="ml-3 mb-1 block text-sm font-medium text-gray-700">Row type</label>
                        <div className="flex">
                            <div className="flex h-6 items-center">
                                <input
                                    id="checkbox_operations"
                                    name="checkbox_operations"
                                    type="checkbox"
                                    checked={showOperations}
                                    onChange={() => setShowOperations((value) => !value)}
                                    className="h-6 w-6 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                />
                                <div className="mx-2">Processed</div>
                            </div>
                            <div className="flex h-6 items-center">
                                <input
                                    id="checkbox_blocked"
                                    name="checkbox_blocked"
                                    type="checkbox"
                                    checked={showBlocked}
                                    onChange={() => setShowBlocked((value) => !value)}
                                    className="h-6 w-6 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                />
                                <div className="mx-2">Blocked</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isLoading && <div className="flex justify-center"><Loader></Loader></div>}
            {error && <div>Error occurred during fetching Accounts: {error.toString()}</div>}
            <table className="min-w-full divide-y divide-gray-200 border-b-2 border-gray-400">
                <thead className="sticky top-64 bg-gray-50 z-30">
                <tr>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex h-5 items-center">
                            <input
                                id="checkbox_all"
                                name="checkbox_all"
                                type="checkbox"
                                checked={allSelected}
                                onChange={handleSelectAll}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                        </div>
                    </th>
                    <th scope="col" className="px-1 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        C/M
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total / Block
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category / Account
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Comment
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tags
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                    </th>
                    <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                    </th>
                </tr>
                </thead>
                {!isLoading && displayGroups.length === 0 && <tbody>
                <tr className="font-bold text-xl text-gray-700">
                    <td colSpan={6}><span className="m-2">
                    {filter === '' && selectedStatuses.length !== 0 ? 'Session has no transaction records' : 'Session has no such transaction records. Please consider to change filtration'}
                </span></td>
                </tr>
                </tbody>}
                {displayGroups.length !== 0 && <tbody className="bg-white divide-y divide-gray-200">
                {displayGroups.map(({parent, children}) =>
                    <BulkUploadRowGroup
                        key={parent.id}
                        parent={parent}
                        childrenRows={children}
                        handleCategoryChange={handleCategoryRowChange}
                        selectedIds={selectedIds}
                        accountCurrencySymbol={session?.currency_symbol}
                        sessionAccountId={session?.account}
                    />
                )}
                </tbody>}
            </table>
        </>
    );
};

export default BulkUploadRowsTable;
